import React, { useState } from 'react';
import { auth, db } from '../firebase';
import { doc, updateDoc } from 'firebase/firestore';
import '../styles/Settings.css';

const IconPencil = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);

function SettingsSection({ companyData, onCompanyUpdate }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(companyData?.name || '');
  const [editedIndustry, setEditedIndustry] = useState(companyData?.industry || '');
  const [editedRegion, setEditedRegion] = useState(companyData?.region || '');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const industries = [
    'Construction', 'Manufacturing', 'Transportation & Logistics', 'Healthcare',
    'Mining', 'Energy & Utilities', 'Agriculture', 'Hospitality', 'Retail',
    'Warehousing', 'Emergency Services', 'Aviation', 'Maritime', 'Other'
  ];

  const countries = [
    'United States', 'United Kingdom', 'Canada', 'Australia', 'Germany', 'France',
    'Spain', 'Italy', 'Netherlands', 'Belgium', 'Switzerland', 'Austria', 'Sweden',
    'Norway', 'Denmark', 'Finland', 'Poland', 'Ireland', 'Portugal', 'Greece',
    'Czech Republic', 'Hungary', 'Romania', 'Bulgaria', 'Croatia', 'Slovakia',
    'Slovenia', 'Luxembourg', 'Estonia', 'Latvia', 'Lithuania', 'Cyprus', 'Malta',
    'Iceland', 'Japan', 'South Korea', 'China', 'India', 'Singapore', 'Hong Kong',
    'Taiwan', 'Thailand', 'Malaysia', 'Indonesia', 'Philippines', 'Vietnam',
    'New Zealand', 'Mexico', 'Brazil', 'Argentina', 'Chile', 'Colombia', 'Peru',
    'South Africa', 'United Arab Emirates', 'Saudi Arabia', 'Israel', 'Turkey',
    'Russia', 'Ukraine', 'Other'
  ].sort();

  const handleEdit = () => {
    setEditedName(companyData.name);
    setEditedIndustry(companyData.industry);
    setEditedRegion(companyData.region || '');
    setIsEditing(true);
    setError('');
  };

  const handleCancel = () => {
    setEditedName(companyData.name);
    setEditedIndustry(companyData.industry);
    setEditedRegion(companyData.region || '');
    setIsEditing(false);
    setError('');
  };

  const sanitizeCompanyName = (name) => name.trim().slice(0, 100).replace(/[<>]/g, '');

  const handleSave = async () => {
    setError('');
    const sanitizedName = sanitizeCompanyName(editedName);
    if (!sanitizedName || sanitizedName.length < 2) { setError('Company name must be at least 2 characters'); return; }
    if (!editedIndustry) { setError('Please select an industry'); return; }
    if (!editedRegion) { setError('Please select a region'); return; }
    if (sanitizedName === companyData.name && editedIndustry === companyData.industry && editedRegion === companyData.region) {
      setIsEditing(false); return;
    }
    setSaving(true);
    try {
      await updateDoc(doc(db, 'companies', companyData.id), {
        name: sanitizedName, industry: editedIndustry, region: editedRegion, updatedAt: new Date().toISOString()
      });
      if (onCompanyUpdate) await onCompanyUpdate();
      setIsEditing(false);
      alert('Company information updated successfully!');
    } catch (err) {
      console.error('Error updating company:', err);
      setError('Failed to update company information. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="dashboard-header">
        <div>
          <h1>Settings</h1>
          <p>Manage your company and account information</p>
        </div>
      </div>

      <div className="settings-content">
        <div className="settings-card">
          <h3>Account Information</h3>
          <div className="info-row">
            <span className="info-label">Email</span>
            <span className="info-value">{auth.currentUser?.email}</span>
          </div>
          <div className="info-row">
            <span className="info-label">Company Code</span>
            <span className="info-value">{companyData?.companyCode}</span>
          </div>
          <p className="info-note">
            Share your company code with employees so they can join your organisation.
          </p>
        </div>

        <div className="settings-card">
          <div className="card-header">
            <h3>Company Information</h3>
            {!isEditing && (
              <button className="btn-edit" onClick={handleEdit}>
                <IconPencil /> Edit
              </button>
            )}
          </div>

          {error && <div className="error-message">{error}</div>}

          {!isEditing ? (
            <>
              <div className="info-row">
                <span className="info-label">Company Name</span>
                <span className="info-value">{companyData?.name}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Industry</span>
                <span className="info-value">{companyData?.industry}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Region</span>
                <span className="info-value">{companyData?.region || 'Not set'}</span>
              </div>
            </>
          ) : (
            <div className="edit-form">
              <div className="form-group">
                <label htmlFor="companyName">Company Name</label>
                <input type="text" id="companyName" value={editedName} onChange={(e) => setEditedName(e.target.value)} placeholder="Enter company name..." maxLength="100" disabled={saving} />
              </div>
              <div className="form-group">
                <label htmlFor="industry">Industry</label>
                <select id="industry" value={editedIndustry} onChange={(e) => setEditedIndustry(e.target.value)} disabled={saving}>
                  <option value="">Select your industry...</option>
                  {industries.map(i => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="region">Region / Country</label>
                <select id="region" value={editedRegion} onChange={(e) => setEditedRegion(e.target.value)} disabled={saving}>
                  <option value="">Select your region...</option>
                  {countries.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-actions">
                <button className="btn-cancel" onClick={handleCancel} disabled={saving}>Cancel</button>
                <button className="btn-save" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default SettingsSection;
